import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { authAPI, vehiclesAPI } from '../api/api';
import '../css/Documents.css';
import { usePermissions } from '../hooks/usePermissions';

const Documents = ({ isSidebarClosed, setIsSidebarClosed }) => {
  const [documents, setDocuments] = useState([]);
  const [archivedDocuments, setArchivedDocuments] = useState([]);
  const [search, setSearch] = useState('');
  const [user, setUser] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const { permissions } = usePermissions();

  const handleDownload = (e, doc) => {
    e.preventDefault();

    try {
      if (!doc.content) {
        alert('A fájl tartalma nem érhető el');
        return;
      }

      // Decode Base64 content
      const binaryString = window.atob(doc.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob and download
      const blob = new Blob([bytes], { type: doc.mime || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Hiba a fájl letöltésekor');
    }
  };

  useEffect(() => {
    const currentUser = authAPI.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      fetchDocuments(currentUser);
    }
  }, []);

  const fetchDocuments = async (user) => {
    try {
      setLoading(true);
      let vehiclesData;

      if (user.role === 'admin' || user.role === 'boss') {
        vehiclesData = await vehiclesAPI.getVehiclesByClient(user.client_id || user.company);
      } else {
        vehiclesData = await vehiclesAPI.getVehiclesByOwner(user.name, user.client_id || user.company);
      }

      const docs = [];
      const archivedDocs = [];

      // Fetch documents for each vehicle
      for (const vehicle of vehiclesData) {
        if (!vehicle.id) continue;

        try {
          const vehicleDocsData = await vehiclesAPI.getVehicleDocuments(vehicle.id);

          if (vehicleDocsData.sections) {
            // Iterate through all sections
            Object.entries(vehicleDocsData.sections).forEach(([sectionKey, section]) => {
              section.files.forEach(file => {
                const docWithCarInfo = {
                  id: file.id,
                  name: file.filename + (file.extension ? `.${file.extension}` : ''),
                  filename: file.filename,
                  extension: file.extension,
                  mime: file.mime,
                  content: file.content,
                  section_name: section.name,
                  brand: vehicle.brand,
                  model: vehicle.model,
                  plate_number: vehicle.plate_number,
                  chassis_number: vehicle.chassis_number,
                  chassis_number17: vehicle.chassis_number17,
                  owner: vehicle.owner,
                  archived: false // For now, all documents are active
                };

                docs.push(docWithCarInfo);
              });
            });
          }
        } catch (error) {
          console.error(`Error fetching documents for vehicle ${vehicle.id}:`, error);
        }
      }

      setDocuments(docs);
      setArchivedDocuments(archivedDocs);
    } catch (error) {
      console.error('Hiba a dokumentumok lekérésekor:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentDocuments = showArchived ? archivedDocuments : documents;

  const filteredDocuments = currentDocuments.filter(doc => {
    const q = search.toLowerCase();
    return (
      !search ||
      (doc.brand && doc.brand.toLowerCase().includes(q)) ||
      (doc.model && doc.model.toLowerCase().includes(q)) ||
      (doc.plate_number && doc.plate_number.toLowerCase().includes(q)) ||
      (doc.chassis_number && doc.chassis_number.toLowerCase().includes(q)) ||
      (doc.chassis_number17 && doc.chassis_number17.toLowerCase().includes(q)) ||
      (doc.owner && doc.owner.toLowerCase().includes(q)) ||
      (doc.section_name && doc.section_name.toLowerCase().includes(q)) ||
      (doc.name && doc.name.toLowerCase().includes(q))
    );
  });

  // Group documents by vehicle
  const vehicleGroups = {};
  filteredDocuments.forEach(doc => {
    const vehicleKey = doc.chassis_number17 || doc.chassis_number;
    if (!vehicleGroups[vehicleKey]) {
      vehicleGroups[vehicleKey] = {
        brand: doc.brand,
        model: doc.model,
        plate_number: doc.plate_number,
        chassis_number: doc.chassis_number,
        chassis_number17: doc.chassis_number17,
        documents: []
      };
    }
    vehicleGroups[vehicleKey].documents.push(doc);
  });

  // Ellenőrizzük a jogosultságot
  if (!permissions.can_see_documents) {
    return (
      <div>
        <Navbar isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
        <Sidebar isCollapsed={isSidebarClosed} setIsCollapsed={setIsSidebarClosed} />
        <div className="page-content">
          <div style={{
            padding: 32, 
            textAlign: 'center',
            fontSize: '1.2rem',
            color: '#721c24',
            background: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '6px',
            margin: '20px'
          }}>
            ⚠️ Nincs jogosultságod a dokumentumok megtekintéséhez.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
      <Sidebar isCollapsed={isSidebarClosed} setIsCollapsed={setIsSidebarClosed} />
      <div className="page-content documents-page">
        <div className="documents-header">
          <h2 className="documents-title">
            {showArchived ? 'Archív Dokumentumok' : 'Dokumentumok'}
          </h2>
          <button 
            className="documents-toggle-btn"
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived ? 'Aktív Dokumentumok' : 'Archív Dokumentumok'}
          </button>
        </div>
        <div className="documents-search-row">
          <input
            className="documents-search-input"
            placeholder="Keresés..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="documents-content">
          {loading ? (
            <div className="documents-empty">
              Dokumentumok betöltése...
            </div>
          ) : Object.keys(vehicleGroups).length === 0 ? (
            <div className="documents-empty">
              {showArchived ? 'Nincs archív dokumentum.' : 'Nincs dokumentum.'}
            </div>
          ) : (
            <div className="vehicle-documents-grid">
              {Object.entries(vehicleGroups).map(([vehicleKey, vehicle]) => (
                <div key={vehicleKey} className="vehicle-document-card">
                  <div className="vehicle-card-header">
                    <h3 className="vehicle-card-title">
                      {vehicle.brand} {vehicle.model}
                    </h3>
                    <div className="vehicle-card-info">
                      <span className="vehicle-info-item">
                        <strong>Rendszám:</strong> {vehicle.plate_number || '---'}
                      </span>
                      <span className="vehicle-info-item">
                        <strong>Alvázszám:</strong> {vehicle.chassis_number17}
                      </span>
                      <span className="vehicle-info-badge">
                        {vehicle.documents.length} dokumentum
                      </span>
                    </div>
                  </div>
                  <div className="vehicle-card-body">
                    <table className="vehicle-documents-table">
                      <thead>
                        <tr>
                          <th>Típus</th>
                          <th>Dokumentum neve</th>
                          <th>Letöltés</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vehicle.documents.map((doc, idx) => (
                          <tr key={doc.id + '-' + idx}>
                            <td data-label="Típus">{doc.section_name}</td>
                            <td data-label="Dokumentum neve">{doc.name}</td>
                            <td data-label="Letöltés">
                              <button
                                onClick={(e) => handleDownload(e, doc)}
                                className="documents-download-btn"
                              >
                                Letöltés
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Documents; 